import React, { useState, useEffect } from "react"; 
import { Flex, Card, Row, Col, Statistic, DatePicker, Typography, Space, Button } from "antd";
import { UserOutlined, DollarOutlined, ArrowUpOutlined, ArrowDownOutlined, EyeOutlined, EyeInvisibleOutlined, CalendarOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useStudents } from '../../hooks/useStudents';
import { useFinance } from '../../hooks/useFinance';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAttendanceReportSummary, fetchStudentPackageReportSummary, getBirthdayStudents } from '../../store/slices/reportsSlice';
import { sendWpMessage } from '../../store/slices/wpConfigSlice';
import { Modal, Input, message as antdMessage } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';


const Dashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const { attendanceReport, studentPackageReport, birthdayStudents, birthdayStudentsLoading } = useSelector((state) => state.reports);
    const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [showFinancialValues, setShowFinancialValues] = useState(false);
    const [msgModalOpen, setMsgModalOpen] = useState(false);
    const [msgLoading, setMsgLoading] = useState(false);
    const [msgText, setMsgText] = useState('');
    
    // Hooks
    const { students, loading: studentsLoading } = useStudents(selectedBranch?.id);
    const { financialData, loading: financeLoading } = useFinance(dateRange[0], dateRange[1]);

    // Yoklama özeti yükle
    useEffect(() => {
        if (selectedBranch) {
            const params = {
                branchId: selectedBranch.id,
                startDate: dateRange[0].toISOString(),
                endDate: dateRange[1].toISOString()
            };
            dispatch(fetchAttendanceReportSummary(params));
        }
    }, [selectedBranch, dateRange, dispatch]);

    // Aidat özeti yükle
    useEffect(() => {
        if (selectedBranch) {
            const params = {
                branchId: selectedBranch.id,
                startDate: dayjs().subtract(1, 'year').startOf('month').toISOString(),
                endDate: dayjs().endOf('month').toISOString()
            };
            dispatch(fetchStudentPackageReportSummary(params));
        }
    }, [selectedBranch, dispatch]);

    // Doğum günü öğrencileri yükle
    useEffect(() => {
        if (selectedBranch) {
            dispatch(getBirthdayStudents(selectedBranch.id));
        }
    }, [selectedBranch, dateRange, dispatch]);

    // Doğum günü mesajı için örnek
    useEffect(() => {
        if (birthdayStudents && birthdayStudents.length > 0) {
            setMsgText(`Sayın veli, çocuğunuzun doğum gününü kutlar, nice sağlıklı ve spor dolu yıllar dileriz!`);
        }
    }, [birthdayStudents]);

    const handleSendBirthdayMsg = async () => {
        setMsgLoading(true);
        try {
            await dispatch(sendWpMessage({
                branchId: selectedBranch.id,
                studentIds: birthdayStudents.map(m => m.studentId),
                message: msgText
            })).unwrap();
            antdMessage.success('Mesaj gönderildi!');
            setMsgModalOpen(false);
        } catch (err) {
            antdMessage.error('Mesaj gönderilemedi!');
        } finally {
            setMsgLoading(false);
        }
    };

    // Finansal değerleri formatla (maskeleme ile)
    const formatFinancialValue = (value) => {
        if (showFinancialValues) {
            return value;
        }
        return '***';
    };

    // Yoklama raporu sayfasına yönlendir
    const handleAttendanceReportClick = () => {
        navigate('/attendance-report');
    };

    // Aidat raporu sayfasına yönlendir
    const handlePackageReportClick = () => {
        navigate('/aidat-report');
    };

    // Eğer seçili branch yoksa uyarı göster
    if (!selectedBranch) {
        return (
            <Flex vertical gap="middle" style={{ padding: '16px' }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <h3>Lütfen önce bir branş seçiniz</h3>
                    <p>Dashboard'u görüntülemek için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                </div>
            </Flex>
        );
    }

    return (
        <Flex vertical gap="large" style={{ padding: '16px' }}>
            
  
            
            {/* Tarih Aralığı Seçici */}
            <Card>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6}>
                        <Typography.Text strong>Dönem Seçimi:</Typography.Text>
                    </Col>
                    <Col xs={24} sm={12} md={18}>
                        <DatePicker.RangePicker
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates)}
                            format="DD/MM/YYYY"
                            placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>
                <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                    <Col>
                        <Typography.Text strong style={{ marginRight: 8 }}>Hızlı Seçim:</Typography.Text>
                    </Col>
                    <Col>
                        <Space size="small" wrap>
                            <Button 
                                size="small" 
                                onClick={() => setDateRange([dayjs().startOf('month'), dayjs().endOf('month')])}
                                type={dateRange[0]?.isSame(dayjs().startOf('month')) ? 'primary' : 'default'}
                            >
                                Bu Ay
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setDateRange([dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')])}
                                type={dateRange[0]?.isSame(dayjs().subtract(1, 'month').startOf('month')) ? 'primary' : 'default'}
                            >
                                Geçen Ay
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setDateRange([dayjs().subtract(3, 'month').startOf('month'), dayjs().endOf('month')])}
                                type={dateRange[0]?.isSame(dayjs().subtract(3, 'month').startOf('month')) ? 'primary' : 'default'}
                            >
                                Son 3 Ay
                            </Button>
                            <Button 
                                size="small" 
                                onClick={() => setDateRange([dayjs().subtract(1, 'year').startOf('month'), dayjs().endOf('month')])}
                                type={dateRange[0]?.isSame(dayjs().subtract(1, 'year').startOf('month')) ? 'primary' : 'default'}
                            >
                                Son 1 Yıl
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Financial Values Toggle */}
            <Row justify="end" style={{ marginBottom: '16px' }}>
                <Col>
                    <Typography.Link
                        onClick={() => setShowFinancialValues(!showFinancialValues)}
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            fontSize: '16px',
                            color: '#f65700'
                        }}
                    >
                        {showFinancialValues ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        {showFinancialValues ? 'Finansal Değerleri Gizle' : 'Finansal Değerleri Göster'}
                    </Typography.Link>
                </Col>
            </Row>

            {/* İstatistik Kartları */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card loading={studentsLoading}>
                        <Statistic
                            title="Toplam Öğrenci"
                            value={students?.length || 0}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card loading={financeLoading}>
                        <Statistic
                            title="Gelir"
                            value={formatFinancialValue(financialData?.totalIncome || 0)}
                            precision={0}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<ArrowUpOutlined />}
                            suffix={showFinancialValues ? "₺" : ""}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card loading={financeLoading}>
                        <Statistic
                            title="Gider"
                            value={formatFinancialValue(financialData?.totalExpense || 0)}
                            precision={0}
                            valueStyle={{ color: '#cf1322' }}
                            prefix={<ArrowDownOutlined />}
                            suffix={showFinancialValues ? "₺" : ""}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card loading={financeLoading}>
                        <Statistic
                            title="Net Kar"
                            value={formatFinancialValue(financialData?.totalProfit || 0)}
                            precision={0}
                            valueStyle={{ color: (financialData?.totalProfit || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                            prefix={<DollarOutlined />}
                            suffix={showFinancialValues ? "₺" : ""}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Rapor Kartları */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        loading={attendanceReport.loading}
                        hoverable
                        onClick={handleAttendanceReportClick}
                        style={{ 
                            cursor: 'pointer',
                            border: '2px solid #f0f0f0',
                            transition: 'all 0.3s ease'
                        }}
                        bodyStyle={{ 
                            padding: '24px',
                            textAlign: 'center'
                        }}
                    >
                        <div style={{ marginBottom: '16px' }}>
                            <CalendarOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                        </div>
                        <Statistic
                            title="Yoklama Özeti"
                            value={attendanceReport.summary?.overallAttendanceRate.toFixed(2) || 0}
                            suffix="%"
                            valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                        />
                        <div style={{ marginTop: '8px' }}>
                            <Typography.Text type="secondary">
                                Toplam: {attendanceReport.summary?.totalStudentCount || 0} Öğrenci
                            </Typography.Text>
                        </div>
                        <div style={{ marginTop: '8px' }}>
                            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                Detayları görüntülemek için tıklayın
                            </Typography.Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card 
                        loading={studentPackageReport.loading}
                        hoverable
                        onClick={handlePackageReportClick}
                        style={{ 
                            cursor: 'pointer',
                            border: '2px solid #f0f0f0',
                            transition: 'all 0.3s ease'
                        }}
                        bodyStyle={{ 
                            padding: '24px',
                            textAlign: 'center'
                        }}
                    >
                        <div style={{ marginBottom: '16px' }}>
                            <CreditCardOutlined style={{ fontSize: '32px', color: '#52c41a' }} />
                        </div>
                        <Statistic
                            title="Aidat Özeti"
                            value={studentPackageReport.summary?.overallPaymentCompletionRate.toFixed(2) || 0}
                            suffix="%"
                            valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                        />
                        <div style={{ marginTop: '8px' }}>
                            <Typography.Text type="secondary">
                                Toplam: {studentPackageReport.summary?.totalStudentCount || 0} Öğrenci
                            </Typography.Text>
                        </div>
                        <div style={{ marginTop: '8px' }}>
                            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                Detayları görüntülemek için tıklayın
                            </Typography.Text>
                        </div>
                    </Card>
                </Col>
            </Row>
            {/* Doğum günü kutusu */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} lg={12}>
                    <Card
                        loading={birthdayStudentsLoading}
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Doğum Günleri</span>
                                <Button
                                    type="primary"
                                    size="small"
                                    disabled={!birthdayStudents || birthdayStudents.length === 0}
                                    onClick={() => setMsgModalOpen(true)}
                                >
                                    Mesaj Gönder
                                </Button>
                            </div>
                        }
                        style={{ marginTop: 16 }}
                        bodyStyle={{ padding: '24px' }}
                    >
                        {birthdayStudents && birthdayStudents.length > 0 ? (
                            <ul style={{ paddingLeft: 16 }}>
                                {birthdayStudents.map(student => (
                                    <li key={student.studentId} style={{ marginBottom: 8 }}>
                                        <b>{student.fullName}</b> - {student.birthdate ? new Date(student.birthdate).toLocaleDateString('tr-TR') : ''}
                                       
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <span style={{ color: '#888' }}>Bugün doğum günü olan öğrenci yok.</span>
                        )}
                        <Modal
                            open={msgModalOpen}
                            onCancel={() => setMsgModalOpen(false)}
                            onOk={() => {
                                Modal.confirm({
                                    title: 'Emin misiniz?',
                                    content: 'Bu mesajı doğum günü olan tüm öğrencilere göndermek istediğinize emin misiniz?',
                                    okText: 'Evet, Gönder',
                                    cancelText: 'Vazgeç',
                                    onOk: handleSendBirthdayMsg,
                                    confirmLoading: msgLoading,
                                    okButtonProps: { style: { background: '#f65700', borderColor: '#f65700' } }
                                });
                            }}
                            okText="Gönder"
                            cancelText="Vazgeç"
                            confirmLoading={msgLoading}
                        >
                            <div style={{ marginTop: 24 }}>
                                <Input.TextArea
                                    rows={4}
                                    value={msgText}
                                    onChange={e => setMsgText(e.target.value)}
                                    placeholder="Mesajınızı yazın..."
                                />
                            </div>
                        </Modal>
                    </Card>
                </Col>
            </Row>
        </Flex>
    );
};

export default Dashboard;